import { createContext, useCallback, useContext, useRef, useState } from "react"
import type { ReactNode } from "react"

/**
 * 저장·오류를 화면 우하단에 잠깐 띄운다.
 *
 * 카드 위 alert 와 달리 스크롤 위치를 타지 않는다 — 긴 화면 아래쪽에서 버튼을 눌러도
 * 결과가 눈에 들어온다.
 */
type ToastItem = { id: number; message: string; tone: "ok" | "danger" }

type ShowToast = (message: string, tone?: "ok" | "danger") => void

const ToastContext = createContext<ShowToast | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const show = useCallback<ShowToast>((message, tone = "ok") => {
    const id = nextId.current++
    setToasts((current) => [...current, { id, message, tone }])
    setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id))
    }, 3500)
  }, [])

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ShowToast {
  const show = useContext(ToastContext)
  if (!show) throw new Error("ToastProvider 안에서만 쓸 수 있습니다")
  return show
}
